import { getPlayUrl, getPlayList } from '../../services/playApi';
import { PlayUrlResponse, HandledVideoItemData, InitialRequestParams } from '@/types/playDetail';
import { NextPageInfo } from '@/types/common';
import { handleVideoList } from './utils/utils';

/**
 * PlayDetail 服务层 - 封装所有与播放详情相关的API调用
 */
export class PlayDetailService {
  /**
   * 请求首屏数据
   * @param cid 内容ID
   * @param vid 视频ID（可选）
   * @returns Promise<PlayUrlResponse['data']>
   */
  static async fetchPlayUrl(cid: string, vid: string, vipInfo?: { is_vip: boolean }): Promise<PlayUrlResponse['data']> {
    try {
      const responseData = await getPlayUrl(cid, vid, vipInfo);
      return responseData as PlayUrlResponse['data'];
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch video information');
    }
  }

  /**
   * 分页请求剧集列表（递归获取所有页面）
   * @param nextPageInfo 下一页信息
   * @param videoList 当前视频列表
   * @returns Promise<{videoList: VideoItemData[], nextPageInfo: NextPageInfo}>
   */
  static async pullSeasonList(
    nextPageInfo: NextPageInfo,
    videoList: HandledVideoItemData[] = []
  ): Promise<{
    videoList: HandledVideoItemData[];
    nextPageInfo: NextPageInfo;
  }> {
    const { has_next_page: hasNextPage = false, data_key: dataKey = '', page_context: pageContext = '' } = nextPageInfo;
    // 如果没有下一页，直接返回当前数据
    if (!hasNextPage) {
      return {
        videoList,
        nextPageInfo,
      };
    }
    try {
      // 获取下一页数据
      const { video_list: videoListNext, next_page_info: nextPageInfoNext } = await getPlayList(dataKey, pageContext);
      const handledVideoListNext = handleVideoList(videoListNext || []);
      const latestVideoList = [...(videoList || []), ...handledVideoListNext];
      // 如果还有下一页，递归获取
      if (nextPageInfoNext?.has_next_page) {
        return await PlayDetailService.pullSeasonList(nextPageInfoNext, latestVideoList);
      }
      return {
        videoList: latestVideoList,
        nextPageInfo: nextPageInfoNext,
      };
    } catch {
      throw new Error('Failed to fetch episode list');
    }
  }

  /**
   * 请求某部season下的所有剧集列表
   * @param seasonItem 季节项信息
   * @returns Promise<{videoList: HandledVideoItemData[], nextPageInfo: NextPageInfo}>
   */
  static async pullAndUpdateSeasonList(seasonItem: InitialRequestParams): Promise<{
    videoList: HandledVideoItemData[];
    nextPageInfo: NextPageInfo;
  }> {
    const { initialVal: defVideoList, nextPageInfo } = seasonItem;

    // 没有下一页数据，直接返回当前数据
    if (!nextPageInfo?.has_next_page) {
      return {
        videoList: defVideoList,
        nextPageInfo: nextPageInfo as NextPageInfo,
      };
    }

    try {
      // 获取所有数据
      const result = await PlayDetailService.pullSeasonList(nextPageInfo, defVideoList);
      return result;
    } catch {
      throw new Error('Failed to fetch episode list');
    }
  }
}
